import {$insertGeneratedNodes} from '@lexical/clipboard';
import {$generateNodesFromDOM} from '@lexical/html';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {useRichTextMediaUpload} from 'components/DSYS/editor/plugins/MediaUploadContext';
import {normalizePastedHtml} from 'components/DSYS/editor/utils/paperPaste';
import {rehostPastedImages} from 'components/DSYS/editor/utils/rehostPastedImages';
import {$getSelection, COMMAND_PRIORITY_CRITICAL, PASTE_COMMAND, PasteCommandType} from 'lexical';
import {useEffect, useRef} from 'react';
import {getAuraPasteAllowedHostnames, getAuraPublicOrigin} from 'utilities';

/**
 * Full-fidelity HTML paste for long-form surfaces (news posts): keeps headings, tables, task
 * lists, quotes, highlights, emoji, floated images and Aura embeds, with Dropbox Paper's
 * clipboard format mapped onto our nodes (see `normalizePastedHtml`).
 *
 * Runs ahead of the generic paste plugins on purpose: `HeadingPastePlugin` flattens headings
 * to bold text for structured surfaces, which is wrong for a document paste.
 */
export const RichPastePlugin = () => {
  const [editor] = useLexicalComposerContext();
  // The default media upload object is rebuilt each render; read it through a ref so the paste
  // command isn't re-registered on every render.
  const mediaUpload = useRichTextMediaUpload();
  const mediaUploadRef = useRef(mediaUpload);
  mediaUploadRef.current = mediaUpload;

  useEffect(
    () =>
      editor.registerCommand(
        PASTE_COMMAND,
        (event: PasteCommandType) => {
          const clipboardData = (event as ClipboardEvent).clipboardData;
          // Copies from another Lexical editor carry exact node JSON, and file pastes (e.g. a copied
          // image) should go through the upload path; let the default handlers take those.
          if (
            !clipboardData ||
            clipboardData.types.includes('application/x-lexical-editor') ||
            clipboardData.files.length > 0
          ) {
            return false;
          }
          const html = clipboardData.getData('text/html');
          if (!html) {
            return false;
          }

          event.preventDefault();
          const normalized = normalizePastedHtml(html, {
            aura: {
              embedOrigin: getAuraPublicOrigin(),
              allowedHostnames: getAuraPasteAllowedHostnames(),
            },
          });
          const dom = new DOMParser().parseFromString(normalized, 'text/html');
          editor.update(
            () => {
              const selection = $getSelection();
              if (selection) {
                $insertGeneratedNodes(editor, $generateNodesFromDOM(editor, dom), selection);
              }
            },
            {
              // Copy images still hosted by Paper into our storage so the post doesn't depend on
              // the source doc. Pasted URLs keep working in the meantime and on failure.
              onUpdate: () => {
                const {importMediaFromUrl, getMediaUrl} = mediaUploadRef.current;
                if (importMediaFromUrl) {
                  rehostPastedImages(editor, importMediaFromUrl, getMediaUrl, (src, error) =>
                    console.warn('Could not copy pasted image', src, error)
                  );
                }
              },
            }
          );
          return true;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
    [editor]
  );

  return null;
};
